import { derived, readonly, writable, type Readable } from 'svelte/store';

import { BaseModel } from '../shared/base-model';
import { buildValidationErrors } from './utils/entitlements-validation';
import { buildFeedDifference } from './utils/entitlements-diff';
import { buildDraftEntitlementsFromInventory } from './utils/entitlements-draft-builder';

import type {
  EntitlementsParams,
  ResourceKey,
  LoadingByKey,
  FeedEntitlementRow,
  ApprovalsByEntitlement,
  EntitlementsMetadataByEntitlement,
  DataFeedMetadataByFeedId,
  InventoryFeedRow,
  ValidationErrors,
  InventoryFeedInput,
} from './entitlements-types';

type FeedIdsRequiringApprovalSet = Set<string>;
type Errors = Record<ResourceKey, string | null>;

const defaultErrors = (): Errors => ({
  entitlements: null,
  expiredEntitlements: null,
  approvals: null,
  entitlementsMetadata: null,
  dataFeedContext: null,
});

const defaultLoading = (): LoadingByKey => ({
  entitlements: false,
  expiredEntitlements: false,
  approvals: false,
  entitlementsMetadata: false,
  dataFeedContext: false,
});

export class EntitlementsModel extends BaseModel<ResourceKey> {
  public readonly data: EntitlementsParams;

  private readonly _errors = writable<Errors>(defaultErrors());
  private readonly _loadingByKey = writable<LoadingByKey>(defaultLoading());
  private readonly _feedInventory = writable<InventoryFeedRow[]>([]);
  private readonly _feedIdsRequiringApprovalSet = writable<FeedIdsRequiringApprovalSet>(new Set());
  private readonly _dataFeedMetadata = writable<DataFeedMetadataByFeedId>({});
  private readonly _savedEntitlements = writable<FeedEntitlementRow[]>([]);
  private readonly _expiredEntitlements = writable<FeedEntitlementRow[]>([]);
  private readonly _draftEntitlements = writable<FeedEntitlementRow[]>([]);
  private readonly _savedApprovals = writable<ApprovalsByEntitlement>({});
  private readonly _savedEntitlementsMetadata = writable<EntitlementsMetadataByEntitlement>({});

  public readonly loadingAny: Readable<boolean> = derived(this._loadingByKey, (s) => Object.values(s).some(Boolean));
  public readonly loadingByKey: Readable<LoadingByKey> = readonly(this._loadingByKey);
  public readonly errors: Readable<Errors> = readonly(this._errors);
  public readonly feedDifference: Readable<Record<string, unknown>>;
  public readonly validationErrors: Readable<ValidationErrors> = derived(
    [this._feedInventory, this._draftEntitlements],
    ([feedInventory, draftEntitlements]) => buildValidationErrors(feedInventory, draftEntitlements)
  );
  public readonly feedInventory: Readable<InventoryFeedRow[]> = readonly(this._feedInventory);
  public readonly feedIdsRequiringApprovalSet: Readable<FeedIdsRequiringApprovalSet> = readonly(this._feedIdsRequiringApprovalSet);
  public readonly dataFeedMetadata: Readable<DataFeedMetadataByFeedId> = readonly(this._dataFeedMetadata);
  public readonly savedEntitlements: Readable<FeedEntitlementRow[]> = readonly(this._savedEntitlements);
  public readonly expiredEntitlements: Readable<FeedEntitlementRow[]> = readonly(this._expiredEntitlements);
  public readonly draftEntitlements: Readable<FeedEntitlementRow[]> = readonly(this._draftEntitlements);
  public readonly savedApprovals: Readable<ApprovalsByEntitlement> = readonly(this._savedApprovals);
  public readonly savedEntitlementsMetadata: Readable<EntitlementsMetadataByEntitlement> = readonly(this._savedEntitlementsMetadata);

  constructor(data: EntitlementsParams) {
    super();
    this.data = data;

    this.feedDifference = derived([this._savedEntitlements, this._draftEntitlements], ([savedEntitlements, draftEntitlements]) =>
      buildFeedDifference(savedEntitlements, draftEntitlements, this.data.permissionId)
    );
  }

  public clearError = (key: ResourceKey) => {
    this._errors.update((errors) => ({ ...errors, [key]: null }));
  };

  public setError = (key: ResourceKey, message: string) => {
    this._errors.update((errors) => ({ ...errors, [key]: message }));
  };

  public setLoadingByKey = (loadingByKey: LoadingByKey) => {
    this._loadingByKey.set(loadingByKey);
  };

  public setEntitlements = (entitlements: FeedEntitlementRow[]) => {
    this._savedEntitlements.set(entitlements);
    this._draftEntitlements.set(entitlements.map((feed) => ({ ...feed })));
  };

  public setExpiredEntitlements = (entitlements: FeedEntitlementRow[]) => {
    this._expiredEntitlements.set(entitlements);
  };

  public setApprovals = (approvals: ApprovalsByEntitlement) => {
    this._savedApprovals.set(approvals);
  };

  public setEntitlementsMetadata = (entitlementsMetadata: EntitlementsMetadataByEntitlement) => {
    this._savedEntitlementsMetadata.set(entitlementsMetadata);
  };

  public setDataFeedContext = (args: {
    feedInventory: InventoryFeedRow[];
    feedIdsRequiringApproval: Array<string>;
    dataFeedMetadata: DataFeedMetadataByFeedId;
  }) => {
    this._feedInventory.set(args.feedInventory);
    this._feedIdsRequiringApprovalSet.set(new Set(args.feedIdsRequiringApproval));
    this._dataFeedMetadata.set(args.dataFeedMetadata);
  };

  public addDraftEntitlements = (feeds: InventoryFeedInput[] = [], options?: { persistSelectedRanges?: boolean }) => {
    if (!feeds.length) {
      return;
    }

    const normalizedFeeds = buildDraftEntitlementsFromInventory(feeds, {
      ...(options?.persistSelectedRanges !== undefined ? { persistSelectedRanges: options.persistSelectedRanges } : {}),
    });

    this._draftEntitlements.update((current) => [...current, ...normalizedFeeds]);
  };

  public updateDraftEntitlement = (rowId: string, patch: Record<string, unknown>) => {
    this._draftEntitlements.update((current) => current.map((feed) => (feed.id === rowId ? { ...feed, ...patch } : feed)));
  };

  public removeDraftEntitlement = (rowId: string) => {
    this._draftEntitlements.update((current) => current.filter((feed) => feed.id !== rowId));
  };
}
